import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation } from "react-router-dom";
import NavigationMyMessages from "../utiles/NavigationMyMessages";
import MessagesList from "../messages/MessagesList";
import MessageForm from "./MessageForm";
import useMessagesApi from "../../../hooks/api/useMessagesApi";
import useBillingApi from "../../../hooks/api/useBillingApi";

const MyMessagesPage = () => {
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [viewMode, setViewMode] = useState(null); // 'preview' | 'edit' | 'create' | null
  const [sortBy, setSortBy] = useState("Name (A-Z)");

  const selectedMessageRef = useRef(null);
  const { fetchMessages, fetchMessageDetails } = useMessagesApi();
  const { fetchPlans } = useBillingApi();

  useEffect(() => {
    selectedMessageRef.current = selectedMessage;
  }, [selectedMessage]);

  const loadDrafts = useCallback(async (selectId, skipLoadingDetails = false) => {
    setLoading(true);
    try {
      const response = await fetchMessages();
      const fetchedMessages = response.data.messages || [];
      setMessages(fetchedMessages);
      if (selectId) {
        const msg = fetchedMessages.find((m) => m.id === selectId);
        if (msg) {
          // First, try to use filePaths from the message object (from get-messages response)
          let filePathsStr = '';
          if (msg?.filePaths && Array.isArray(msg.filePaths)) {
            // Use filePaths from the message object if available
            const paths = msg.filePaths
              .map((f) => f?.path)
              .filter(Boolean);
            filePathsStr = paths.join(',');
          }
          
          // Load details function - can be called with or without loading state
          const loadMessageDetails = async (showLoading = true) => {
            try {
              if (showLoading) {
                setIsLoadingDetails(true);
              }
              const detailsResp = await fetchMessageDetails(msg.id);
              const fetched = detailsResp.data?.message;
              
              // Use file_paths from details if filePaths wasn't available in message
              let filePathsArray = [];
              if (!filePathsStr && fetched?.file_paths && Array.isArray(fetched.file_paths)) {
                const paths = fetched.file_paths.map((f) => f?.path).filter(Boolean);
                filePathsStr = paths.join(',');
                filePathsArray = fetched.file_paths; // Store full array with size info
              } else if (fetched?.file_paths && Array.isArray(fetched.file_paths)) {
                // Even if we have filePathsStr, store the full array for size info
                filePathsArray = fetched.file_paths;
              }
              
              const initialData = {
                label: fetched?.label || msg.label || '',
                recipient_email: fetched?.recipient_email || msg.recipient_email || '',
                subject: fetched?.subject || msg.subject || '',
                body: fetched?.body || msg.body || '',
                file_paths: filePathsStr,
                file_paths_array: filePathsArray, // Include full array with size info
              };
              setSelectedMessage({ id: msg.id, ...initialData });
              if (showLoading) {
                setViewMode('preview');
              }
              return initialData;
            } catch (e) {
              console.error('Failed to load message details, using message data', e);
              const errorMessage = e?.response?.data?.error || e?.message || 'Failed to load message details';
              toast.error(errorMessage);
              const initialData = {
                label: msg.label || '',
                recipient_email: msg.recipient_email || '',
                subject: msg.subject || '',
                body: msg.body || '',
                file_paths: filePathsStr,
              };
              setSelectedMessage({ id: msg.id, ...initialData });
              if (showLoading) {
                setViewMode('preview');
              }
              return initialData;
            } finally {
              if (showLoading) {
                setIsLoadingDetails(false);
              }
            }
          };
          
          // If skipLoadingDetails is true and we already have the message selected,
          // reload details in background without showing loading state
          const currentSelected = selectedMessageRef.current;
          if (skipLoadingDetails && currentSelected && currentSelected.id === selectId) {
            // Reload details in background to update the message data
            loadMessageDetails(false).catch(err => {
              console.error('Failed to reload message details in background', err);
            });
            return; // Exit early, form stays visible
          }
          
          // Normal flow: load details with loading state
          await loadMessageDetails(!skipLoadingDetails);
        } else {
          setSelectedMessage(null);
          setViewMode(null);
        }
      } else {
        setSelectedMessage(null);
        setViewMode(null);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      const errorMessage = error?.response?.data?.error || error?.message || "Failed to load messages";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fetchMessages, fetchMessageDetails]);

  useEffect(() => {
    // Check if there's a messageId in location state (from search)
    const messageId = location.state?.messageId;
    loadDrafts(messageId);
    // Clear the state after using it
    if (messageId && location.state) {
      window.history.replaceState({}, document.title);
    }
  }, [location, loadDrafts]);

  const handleSelectMessage = async (message) => {
    // Prevent flicker: set message immediately with available data
    let filePathsStr = '';
    if (message?.filePaths && Array.isArray(message.filePaths)) {
      const paths = message.filePaths
        .map((f) => f?.path)
        .filter(Boolean);
      filePathsStr = paths.join(',');
    }
    
    // Set initial data immediately to show message without flicker
    const initialDataImmediate = {
      label: message.label || '',
      recipient_email: message.recipient_email || '',
      subject: message.subject || '',
      body: message.body || '',
      file_paths: filePathsStr,
      file_paths_array: message.filePaths || [],
    };
    
    setSelectedMessage({ id: message.id, ...initialDataImmediate });
    setViewMode('preview');
    
    // Load full details in background and update when ready
    try {
      const detailsResp = await fetchMessageDetails(message.id);
      const fetched = detailsResp.data?.message;
      
      // Use file_paths from details if filePaths wasn't available in message
      let filePathsArray = [];
      if (!filePathsStr && fetched?.file_paths && Array.isArray(fetched.file_paths)) {
        const paths = fetched.file_paths.map((f) => f?.path).filter(Boolean);
        filePathsStr = paths.join(',');
        filePathsArray = fetched.file_paths;
      } else if (fetched?.file_paths && Array.isArray(fetched.file_paths)) {
        filePathsArray = fetched.file_paths;
      }
      
      // Update with full details
      const initialDataComplete = {
        label: fetched?.label || message.label || '',
        recipient_email: fetched?.recipient_email || message.recipient_email || '',
        subject: fetched?.subject || message.subject || '',
        body: fetched?.body || message.body || '',
        file_paths: filePathsStr,
        file_paths_array: filePathsArray,
      };
      
      // Only update if this is still the selected message
      setSelectedMessage((prev) => {
        if (prev?.id === message.id) {
          return { id: message.id, ...initialDataComplete };
        }
        return prev;
      });
    } catch (detailsError) {
      console.error('Failed to load message details, using message data', detailsError);
      const errorMessage = detailsError?.response?.data?.error || detailsError?.message || 'Failed to load message details';
      toast.error(errorMessage);
      // Keep the initial data we already set
    }
  };

  const handleEditMessage = () => {
    setViewMode('edit');
  };

  const handleNewMessage = () => {
    setViewMode('create');
    setSelectedMessage(null);
  };

  const handleMessageSuccess = async (editedId) => {
    // Switch to preview mode immediately to show updated form
    setViewMode('preview');
    // Reload messages list and details in background without showing loading state
    // This prevents flicker by keeping the form visible while updating data
    await loadDrafts(editedId, true); // skipLoadingDetails = true
    try { await fetchPlans(); } catch (e) { /* ignore */ }
  };

  const handleDiscardComposer = () => {
    if (selectedMessage) {
      if (viewMode === 'preview') {
        setViewMode(null);
        setSelectedMessage(null);
      } else {
      setViewMode('preview');
      }
    } else {
      setViewMode(null);
      setSelectedMessage(null);
    }
  };

  const handleDeleteSuccess = async () => {
    await loadDrafts();
    setSelectedMessage(null);
    setViewMode(null);
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
  };

  // Sort messages based on selected option
  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => {
      switch (sortBy) {
        case "Name (A-Z)":
          return (a.label || a.subject || '').localeCompare(b.label || b.subject || '');
        case "Date":
          // Sort by date (newest first) - using created_at or updated_at
          const dateA = new Date(a.created_at || a.updated_at || 0);
          const dateB = new Date(b.created_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime(); // Newest first
        case "File Size":
          // Sort by file size if available
          return (a.fileSize || 0) - (b.fileSize || 0);
        default:
          return 0;
      }
    });
  }, [messages, sortBy]);


  return (
    <div className="flex flex-col h-full  overflow-scroll desktop:overflow-hidden">
      {/* Desktop Navigation - always visible */}
      <div className="hidden md:block">
        <NavigationMyMessages onNewMessage={handleNewMessage} />
      </div>
      {/* Mobile - show list or composer based on state */}
      <div className="md:hidden">
        {!viewMode ? (
          <>
            <NavigationMyMessages onNewMessage={handleNewMessage} />
            <div className="mt-4 mobile:mt-0">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray5">Loading drafts...</p>
                </div>
              ) : (
                <MessagesList
                  messages={sortedMessages}
                  selectedMessageId={selectedMessage?.id}
                  onSelectMessage={handleSelectMessage}
                  title="My messages"
                  onNewMessage={handleNewMessage}
                  onSortChange={handleSortChange}
                  onDelete={handleDeleteSuccess}
                />
              )}
            </div>
          </>
        ) : (
          <div className="h-full">
            {isLoadingDetails ? (
              <div className="flex items-center justify-center h-full bg-white rounded-lg">
                <p className="text-gray5">Loading message details...</p>
              </div>
            ) : (
              <MessageForm
                key={`${viewMode}-${selectedMessage?.id || 'create'}`}
                mode={viewMode}
                onSuccess={handleMessageSuccess}
                onDiscard={handleDiscardComposer}
                onEdit={handleEditMessage}
                initialData={selectedMessage}
                messageId={selectedMessage?.id}
              />
            )}
          </div>
        )}
      </div>

      <div className="hidden md:flex flex-1 gap-2 overflow-hidden mt-4">
        <div className="w-1/3 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray5">Loading drafts...</p>
            </div>
          ) : (
            <MessagesList
              messages={sortedMessages}
              selectedMessageId={selectedMessage?.id}
              onSelectMessage={handleSelectMessage}
              title="My messages"
              onSortChange={handleSortChange}
              onDelete={handleDeleteSuccess}
            />
          )}
        </div>

        <div className="flex-1 rounded-lg overflow-hidden">
          {isLoadingDetails ? (
            <div className="flex items-center justify-center h-full bg-white rounded-lg">
              <p className="text-gray5">Loading message details...</p>
            </div>
          ) : viewMode ? (
            <MessageForm
              key={`${viewMode}-${selectedMessage?.id || 'create'}`}
              mode={viewMode}
              onSuccess={handleMessageSuccess}
              onDiscard={handleDiscardComposer}
              onEdit={handleEditMessage}
              initialData={selectedMessage}
              messageId={selectedMessage?.id}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-white rounded-lg">
              <img src={`${process.env.PUBLIC_URL}/envelope-orange.svg`} className="" alt="envelopes" />
              <h3 className="text-dark5 text-h6 font-semibold">No message selected</h3>
              <p className="text-dark5 text-bodyMR mt-2">Select or create a message</p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default MyMessagesPage;